// RisographGalleryWalk — Tier-A "Risograph Assembly" renderer for the
// gallery-walk component (the world's SECOND home-set pairing, after
// perspective-lenses).
//
// Reuses the existing Risograph toolkit registration (atmosphere + print-shop
// audio cue palette in lib/scenario-themes/) — no new world infra. Reframes the
// gallery walk as a ZINE EXHIBITION SPREAD: each station is a torn clipping pinned
// to the wall in a spot ink; annotating it (writing the reflection) stamps it
// "annotated"; once every exhibit is annotated, the editor's note goes to press.
//
// State machine + onActivityResponse mirror GalleryWalkSlide's Student exactly:
// `openStationId` + `openedIds` + `reflections` + `synthesis` + `submitted`;
// "visited" = stations that have been OPENED (monotonic), NOT stations with a
// reflection. On submit it emits the EXACT
//   { activityType:'gallery-walk', mode:'survey',
//     responseData:{ visited, reflections } } (+ synthesis only when authored)
// payload, with trimmed values.

import { useState, useCallback, useEffect, useRef } from 'react';
import type { GalleryWalkSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';

const STYLE_ID = 'riso-gw-scoped-styles';

const INKS = ['#ff48a0', '#2453ff', '#00a878', '#ff6a13'];
const inkOf = (i: number) => INKS[i % INKS.length];
const CREAM = '#f4efe2';
const INK = '#16130d';
const PAPER_SHADOW = 'rgba(22,19,13,.22)';
const TAB_ROT = ['-1.5deg', '1.2deg', '-1deg', '1.6deg'];

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — namespaced `.risogw`; injected once, ref-counted.
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(): string {
  return `
.risogw { position: relative; --reg: 2px; }
.risogw * { box-sizing: border-box; }

.risogw .rgw-disp { font-family: "Anton", "Archivo Black", sans-serif; text-transform: uppercase; letter-spacing: .5px; line-height: .92; position: relative; display: inline-block; }
.risogw .rgw-disp[data-ghost]::after { content: attr(data-ghost); position: absolute; left: var(--reg); top: var(--reg); width: 100%; z-index: -1; mix-blend-mode: multiply; color: var(--ghost, ${INK}); pointer-events: none; }
/* ^ width: 100% pins the ghost's wrap width to the original's, so the two copies
   can never line-break differently and overlap the content below. */

.risogw .rgw-kicker { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 6px; font-size: 13px; color: ${INK}; border-top: 3px solid ${INK}; border-bottom: 3px solid ${INK}; display: block; max-width: 760px; margin: 0 auto; padding: 5px 16px 3px; transform: rotate(-1deg); }
.risogw .rgw-intro { font-size: 1rem; line-height: 1.5; color: ${INK}; max-width: 620px; margin: 14px auto 0; text-align: center; }
.risogw .rgw-progress { text-align: center; margin: 18px auto 4px; font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 2px; font-size: 13px; }
.risogw .rgw-progress b { color: ${INKS[3]}; }

.risogw .rgw-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 24px; }
@media (max-width: 640px) { .risogw .rgw-grid { grid-template-columns: 1fr; } }

.risogw .rgw-clip { position: relative; background: ${CREAM}; border: 2.5px solid; box-shadow: 6px 7px 0 ${PAPER_SHADOW}; padding: 18px 20px 20px; }
.risogw .rgw-clip.open { grid-column: 1 / -1; }
.risogw .rgw-tape { position: absolute; top: -14px; left: 50%; width: 96px; height: 26px; transform: translateX(-50%) rotate(-3deg); background: rgba(255,106,19,.5); border-left: 1px dashed rgba(22,19,13,.4); border-right: 1px dashed rgba(22,19,13,.4); mix-blend-mode: multiply; }
.risogw .rgw-station-no { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 2px; font-size: 11px; }
.risogw .rgw-station-title { font-size: clamp(20px, 3.5vw, 30px); margin: 4px 0 6px; cursor: pointer; }
.risogw .rgw-teaser { font-size: .9rem; color: #5e5544; margin: 0; }
.risogw .rgw-annotated { position: absolute; top: -10px; right: -10px; font-family: "Anton", sans-serif; font-size: 12px; color: ${INKS[2]}; border: 2.5px solid ${INKS[2]}; border-radius: 4px; padding: 3px 7px 1px; transform: rotate(-12deg); background: ${CREAM}; }

.risogw .rgw-body { margin-top: 14px; padding-top: 14px; border-top: 1.5px dashed rgba(22,19,13,.25); animation: rgwIn .35s ease both; }
@keyframes rgwIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.risogw .rgw-block { font-family: "Newsreader", serif; font-size: 1rem; line-height: 1.55; color: ${INK}; margin: 0 0 12px; }
.risogw .rgw-quote { border-left: 4px solid var(--accent-ink, ${INK}); padding: 2px 0 2px 14px; margin: 0 0 12px; }
.risogw .rgw-quote p { font-family: "Newsreader", serif; font-style: italic; font-size: 1.08rem; line-height: 1.4; color: ${INK}; margin: 0; }
.risogw .rgw-quote .src { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 1px; font-size: 11px; color: #5e5544; margin-top: 6px; }
.risogw .rgw-img { width: 100%; border: 2px solid ${INK}; filter: grayscale(.3) contrast(1.05); margin-bottom: 6px; }

.risogw .rgw-note-label { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 1.5px; font-size: 12px; display: block; margin: 6px 0 6px; }
.risogw textarea.rgw-note { width: 100%; font-family: "Newsreader", serif; font-size: 16px; background: rgba(255,255,255,.5); border: 2.5px solid; color: ${INK}; padding: 10px 12px; resize: vertical; min-height: 64px; }
.risogw textarea.rgw-note:focus { outline: none; box-shadow: 3px 3px 0 currentColor; }

.risogw .rgw-synthesis { margin-top: 30px; background: ${CREAM}; border: 4px solid ${INK}; box-shadow: 10px 12px 0 ${INK}; padding: 28px 30px 32px; position: relative; animation: rgwIn .45s ease both; }
.risogw .rgw-filed { position: absolute; top: 16px; right: 20px; font-family: "Anton", sans-serif; font-size: 18px; letter-spacing: 1px; color: ${INKS[0]}; border: 4px solid ${INKS[0]}; border-radius: 6px; padding: 5px 10px 3px; transform: rotate(-12deg); mix-blend-mode: multiply; }
.risogw .rgw-synthesis h2 { font-size: clamp(28px, 6vw, 56px); margin: 0 0 14px; }
.risogw .rgw-press-btn { margin-top: 14px; font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 1.5px; font-size: 17px; cursor: pointer; color: ${CREAM}; border: 3px solid ${INK}; padding: 12px 22px 10px; box-shadow: 4px 5px 0 ${INK}; transition: transform .08s, box-shadow .08s; }
.risogw .rgw-press-btn:hover:not(:disabled) { transform: translate(-1px,-1px); box-shadow: 6px 7px 0 ${INK}; }
.risogw .rgw-press-btn:disabled { opacity: .45; cursor: not-allowed; }
.risogw .rgw-printed { text-align: center; margin-top: 24px; font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 3px; font-size: 22px; color: ${INKS[1]}; }

@media (prefers-reduced-motion: reduce) {
  .risogw .rgw-body, .risogw .rgw-synthesis { animation: none !important; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function RisoHeadline({ text, ghost, style }: { text: string; ghost: string; style?: React.CSSProperties }) {
  return (
    <span className="rgw-disp" data-ghost={text} style={{ ['--ghost' as string]: ghost, ...style }}>
      {text}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function RisographGalleryWalk({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: GalleryWalkSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  useScopedStyles();
  const { enabled: audioOn, toggle: toggleAudio, play } = useThemeWorldAudio('risograph');
  const headingFont = theme.fonts.heading;

  // The {visited, reflections, synthesis} payload only fires on submit, so its
  // presence means the walk finished — restore the submitted view from it.
  const saved: Record<string, unknown> = savedResponse ?? {};
  const restored = Array.isArray(saved.visited);

  // ── State machine (mirrors GalleryWalkSlide's Student) ───────────────────────
  const [openStationId, setOpenStationId] = useState<string | null>(null);
  // A station counts as visited once OPENED; the set is monotonic. Restored from
  // the persisted `visited` array.
  const [openedIds, setOpenedIds] = useState<Set<string>>(
    () => new Set(restored ? (saved.visited as string[]) : []),
  );
  const [reflections, setReflections] = useState<Record<string, string>>(() =>
    restored && saved.reflections && typeof saved.reflections === 'object'
      ? { ...(saved.reflections as Record<string, string>) }
      : {},
  );
  const [synthesis, setSynthesis] = useState(
    restored && typeof saved.synthesis === 'string' ? saved.synthesis : '',
  );
  const [submitted, setSubmitted] = useState(restored);
  // Fire onComplete exactly once at the terminal submit ("Go to press").
  const completedRef = useRef(restored);

  // "visited" = stations OPENED, monotonic. Stations with a non-empty reflection
  // prompt additionally need a non-empty reflection before the walk can finish.
  const visited = data.stations.filter((s) => openedIds.has(s.id)).map((s) => s.id);
  const allVisited = visited.length === data.stations.length && data.stations.length > 0;
  const reflectionsDone = data.stations.every(
    (s) => s.reflectionPrompt.trim().length === 0 || (reflections[s.id] ?? '').trim().length > 0,
  );
  const canFinish = allVisited && reflectionsDone;
  const hasSynthesisPrompt = (data.synthesis?.prompt ?? '').trim().length > 0;

  const lastTickRef = useRef(0);
  const onNoteInput = (id: string, value: string) => {
    setReflections((prev) => ({ ...prev, [id]: value }));
    const now = (globalThis.performance?.now?.() ?? 0);
    if (now - lastTickRef.current > 60) { play('hover'); lastTickRef.current = now; } // typewriter tick
  };

  const openStation = (id: string) => {
    setOpenStationId((cur) => {
      const next = cur === id ? null : id;
      if (next) {
        // Opening a station marks it visited — the set is monotonic.
        setOpenedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
        play('select'); // paper rustle
      }
      return next;
    });
  };

  const submit = useCallback(() => {
    setSubmitted(true);
    play('commit'); // stamp
    window.setTimeout(() => play('reveal'), 280); // print-roller ka-chunk
    const trimmedReflections = Object.fromEntries(
      Object.entries(reflections).map(([id, v]) => [id, v.trim()]),
    );
    const responseData: Record<string, unknown> = { visited, reflections: trimmedReflections };
    // Only include synthesis when the author actually set a prompt.
    if (hasSynthesisPrompt) responseData.synthesis = synthesis.trim();
    onActivityResponse?.({ activityType: 'gallery-walk', mode: 'survey', responseData });
    // Terminal completion point: going to press is the single submit.
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [visited, reflections, synthesis, hasSynthesisPrompt, onActivityResponse, onComplete, play]);

  return (
    <div className="risogw h-full w-full overflow-y-auto" style={{ background: theme.colors.background, color: INK, fontFamily: theme.fonts.body }}>
      <ThemeWorldAtmosphere worldId="risograph" primary={INKS[1]} accent={INKS[0]} background={theme.colors.background} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 2 }}>
        <div style={{ maxWidth: 820, margin: '0 auto', padding: '78px 22px 100px' }}>
          {/* masthead */}
          <header style={{ textAlign: 'center', marginBottom: 6 }}>
            <span className="rgw-kicker">The Assembly Press — Gallery Edition</span>
            <h1 style={{ margin: '16px 0 0', fontSize: 'clamp(30px, 6vw, 64px)' }}>
              <RisoHeadline text={data.title} ghost={INKS[0]} style={{ color: INK }} />
            </h1>
            {data.subtitle && <p style={{ fontFamily: '"Newsreader", serif', fontStyle: 'italic', fontSize: '1rem', color: '#5e5544', margin: '8px 0 0' }}>{data.subtitle}</p>}
            {data.intro && <p className="rgw-intro">{data.intro}</p>}
          </header>

          {data.stations.length === 0 ? (
            <div className="rgw-intro" style={{ marginTop: 24 }}>
              This gallery has no exhibits yet — there is nothing to explore.
            </div>
          ) : (
            <div className="rgw-progress">
              Exhibits visited: <b>{visited.length}</b> / {data.stations.length}
            </div>
          )}

          {/* exhibits */}
          <div className="rgw-grid">
            {data.stations.map((station, idx) => {
              const ink = inkOf(idx);
              const isOpen = openStationId === station.id;
              const isVisited = openedIds.has(station.id);
              // The "Annotated" stamp stays tied to a written reflection (aesthetic),
              // distinct from "visited" (opened) which drives completion.
              const isAnnotated = (reflections[station.id] ?? '').trim().length > 0;
              return (
                <article
                  key={station.id}
                  className={`rgw-clip${isOpen ? ' open' : ''}`}
                  style={{ borderColor: ink, transform: isOpen ? 'none' : `rotate(${TAB_ROT[idx % TAB_ROT.length]})` }}
                >
                  {isOpen && <span className="rgw-tape" />}
                  {isAnnotated && <span className="rgw-annotated">✓ Annotated</span>}
                  <div className="rgw-station-no" style={{ color: ink }}>Exhibit {String(idx + 1).padStart(2, '0')}</div>
                  {/* Station titles are plain Anton (no misregistration): short titles
                      wrap inside the narrow cards, where the offset ghost would mismatch.
                      Misregistration is reserved for the masthead + synthesis headlines. */}
                  <h3 className="rgw-station-title rgw-disp" style={{ fontFamily: headingFont, color: INK }} onClick={() => openStation(station.id)} role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openStation(station.id); } }}>
                    {station.title}
                  </h3>
                  <p className="rgw-teaser">{station.teaser}</p>

                  {isOpen && (
                    <div className="rgw-body">
                      {station.content.map((block, bIdx) => {
                        if (block.type === 'image') {
                          return (
                            <figure key={bIdx} style={{ margin: '0 0 12px' }}>
                              <img className="rgw-img" src={block.value} alt={block.attribution ?? ''} />
                              {block.attribution && <figcaption style={{ fontFamily: '"Anton", sans-serif', textTransform: 'uppercase', letterSpacing: '1px', fontSize: 11, color: '#5e5544' }}>{block.attribution}</figcaption>}
                            </figure>
                          );
                        }
                        if (block.type === 'quote') {
                          return (
                            <blockquote key={bIdx} className="rgw-quote" style={{ ['--accent-ink' as string]: ink }}>
                              <p>“{block.value}”</p>
                              {block.attribution && <div className="src" style={{ color: ink }}>— {block.attribution}</div>}
                            </blockquote>
                          );
                        }
                        return <p key={bIdx} className="rgw-block">{block.value}</p>;
                      })}
                      {station.reflectionPrompt.trim().length > 0 && (
                        <>
                          <label className="rgw-note-label" style={{ color: ink }}>{station.reflectionPrompt}</label>
                          <textarea
                            className="rgw-note"
                            style={{ borderColor: ink }}
                            rows={3}
                            value={reflections[station.id] ?? ''}
                            disabled={submitted}
                            placeholder="Pencil your note in the margin…"
                            onChange={(e) => onNoteInput(station.id, e.target.value)}
                          />
                        </>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {/* synthesis / editor's note — appears once the walk can finish (all
              exhibits opened + any required reflections written). The synthesis
              note is OPTIONAL: "Go to press" is never gated on it, so an empty
              prompt can never block completion. When the author set no synthesis
              prompt we still surface a plain finish button so the walk can end. */}
          {canFinish && (
            <section className="rgw-synthesis">
              {submitted && <div className="rgw-filed">Printed</div>}
              {hasSynthesisPrompt ? (
                <>
                  <h2 style={{ fontFamily: headingFont }}>
                    <RisoHeadline text="Editor's Note" ghost={INKS[3]} style={{ color: INKS[1] }} />
                  </h2>
                  <label className="rgw-note-label" style={{ color: INKS[3] }}>{data.synthesis!.prompt}</label>
                  <textarea
                    className="rgw-note"
                    style={{ borderColor: INK }}
                    rows={4}
                    value={synthesis}
                    disabled={submitted}
                    placeholder="Set the closing column…"
                    onChange={(e) => { setSynthesis(e.target.value); const now = globalThis.performance?.now?.() ?? 0; if (now - lastTickRef.current > 60) { play('hover'); lastTickRef.current = now; } }}
                  />
                  {!submitted ? (
                    <button type="button" className="rgw-press-btn" style={{ background: INKS[3] }} onClick={submit}>
                      ▣ Go to press
                    </button>
                  ) : (
                    <div className="rgw-printed">★ Off the press — walk complete ★</div>
                  )}
                </>
              ) : !submitted ? (
                <button type="button" className="rgw-press-btn" style={{ background: INKS[3] }} onClick={submit}>
                  ▣ Go to press
                </button>
              ) : (
                <div className="rgw-printed">★ Off the press — walk complete ★</div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
